import React, { useEffect, useState, useRef } from "react";
import { useDAWStore } from "../../store/dawStore";
import engine from "../../audio/AudioEngine";
import { Trash, MagnifyingGlassPlus, MagnifyingGlassMinus, GearSix, ArrowsOutLineHorizontal, ArrowsOutLineVertical, PaintBrush, Cursor } from "@phosphor-icons/react";

// Vertical range: C1 → B7 (7 octaves, 84 keys). Tone names are highest-first
// so the grid renders B7 at the top and C1 at the bottom — matching how piano
// rolls draw pitch (top = higher). Earlier versions only spanned C3→B5; the
// expanded range lets bass parts (C1-B2) and high leads (C6-B7) be edited.
const NOTES = (() => {
  const arr = [];
  const names = ["B","A#","A","G#","G","F#","F","E","D#","D","C#","C"];
  for (let oct = 7; oct >= 1; oct--) {
    for (let i = 0; i < names.length; i++) arr.push(`${names[i]}${oct}`);
  }
  return arr; // 84 notes total
})();

// v12.6 — Base steps-per-bar (1/16 resolution). The actual grid width is
// derived at runtime as `max(BASE_STEPS_PER_BAR * 4, region.length * BASE_STEPS_PER_BAR)`
// so producers ALWAYS have a full 4-bar canvas to paint on, even when the
// current region is short. Notes dropped past the region's current end
// auto-extend the region so the arrangement grid stays in sync.
const BASE_STEPS_PER_BAR = 16;
const MIN_BARS_VISIBLE = 4;
const STEPS = BASE_STEPS_PER_BAR; // legacy alias used by some math sites; kept for compat
const HEADER_WIDTH = 70;

// v19.12 — Transpose a pitch string (`C4`, `F#3`) by `semis` semitones.
// Uses the same NOTES array as the piano grid so the returned pitch is
// always a valid row in the grid. Clamped to `[NOTES[last], NOTES[0]]`.
function _transpose(pitch, semis) {
  const idx = NOTES.indexOf(pitch);
  if (idx < 0) return pitch;
  // NOTES is highest→lowest, so shifting UP in pitch means DECREASING the index.
  const newIdx = Math.max(0, Math.min(NOTES.length - 1, idx - semis));
  return NOTES[newIdx];
}

// Convert paint-grid step-count → user-facing label (used in the toolbar hint).
const paintGridLabel = (size) => {
  if (size >= 2) return "1/8";
  if (size >= 1) return "1/16";
  if (size >= 0.5) return "1/32";
  return "1/64";
};

export default function PianoRoll() {
  const { tracks, regions, selectedRegionId, selectRegion,
          addRegionNote, removeRegionNote, updateRegionNote, clearRegionNotes,
          _snapshot } = useDAWStore();
  const [playingStep, setPlayingStep] = useState(-1);
  const [stepWidth, setStepWidth] = useState(36);
  const [rowHeight, setRowHeight] = useState(18);
  const [showZoom, setShowZoom] = useState(false);
  // v12.5 — Fit exactly 4 bars in the visible viewport on first paint,
  // matching standard DAW behaviour (Ableton, Logic, FL Studio all show
  // the full context of a typical MIDI region). Measured once via a
  // ResizeObserver hooked on the grid scroll container; subsequent zoom
  // changes are producer-controlled via the +/- slider.
  const scrollerRef = useRef(null);
  const didFitRef = useRef(false);
  useEffect(() => {
    if (didFitRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    // v12.6 — Fit the FULL 4-bar (64-step) grid inside the visible width,
    // subtracting the fixed piano-key header column. Clamp to a 20px
    // minimum so on very narrow windows the grid scrolls horizontally
    // rather than becoming unusably tiny.
    const visibleWidth = Math.max(400, el.clientWidth - HEADER_WIDTH);
    const target = Math.floor(visibleWidth / (MIN_BARS_VISIBLE * BASE_STEPS_PER_BAR));
    const clamped = Math.max(20, Math.min(120, target));
    if (clamped && clamped !== stepWidth) {
      setStepWidth(clamped);
    }
    didFitRef.current = true;
  }, [stepWidth]);
  // Tool + quantization state.
  // `tool = 'select'` → classic behaviour: click to add/remove a note (default 1-step wide).
  // `tool = 'paint'`  → paintbrush: click OR drag across the grid to lay down
  //   a stream of notes at the `paintGrid` resolution — 1/8, 1/16, 1/32, 1/64.
  //   The paint grid step-size is in fractional steps (2 for 1/8, 1 for 1/16,
  //   0.5 for 1/32, 0.25 for 1/64). Setting a finer grid unlocks fast rolls
  //   (32nd/64th note snare fills, hi-hat trills) that snap cleanly.
  const [tool, setTool] = useState("select");
  const [paintGridSize, setPaintGridSize] = useState(1); // in step units — 1 = 1/16
  const paintingRef = useRef(null); // { lastKey, lastPitch, lastStep }
  const gridRef = useRef(null);

  // v13.1 — Quantize-to-Scale key selection. 24 options (12 tonics × maj/min).
  // Empty string = "no scale" (Quantize-to-Scale button is a no-op until a
  // scale is picked).
  const [scaleKey, setScaleKey] = useState("");
  // v13.1 — Velocity lane strip height (pixels) at the bottom of the grid.
  const VELOCITY_LANE_HEIGHT = 90;

  // Marquee bounding-box selection (v9.3). While tool='select' and the user
  // click-drags on an EMPTY grid cell (not on an existing note), we draw a
  // rectangle and — on mouseup — mark every note inside it as `selectedIds`.
  // Selected notes render with a bright ring outline and can be batch-
  // Deleted (Backspace/Delete key) or batch-Duplicated (Ctrl/Cmd+D). The
  // duplicate places clones one paintGridSize (default 1 step) to the right
  // so a rolled fill-hi-hat can be extended with one shortcut.
  const [marquee, setMarquee] = useState(null); // { x0, y0, x1, y1 } while dragging
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Region-locked: piano roll edits regions on synth/sampler/bass/melody tracks,
  // plus any drum-sequencer track that the user has converted via "Convert to
  // Piano Roll".
  const melodicRegions = regions.filter((r) => {
    const t = tracks.find((tr) => tr.id === r.trackId);
    if (!t) return false;
    return t.type === "synth" || t.type === "sampler" || t.type === "bass" || t.type === "melody" || t.viewMode === "pr";
  });
  const activeRegion = melodicRegions.find((r) => r.id === selectedRegionId) || null;
  const activeTrack = activeRegion ? tracks.find((t) => t.id === activeRegion.trackId) : null;

  // v12.6 — Full-region grid width. Always render AT LEAST 4 bars (64
  // steps at 1/16 resolution) so producers can paint notes across the
  // whole span without horizontal scrolling on a normal monitor. When
  // the region is longer than 4 bars, the grid matches it. This is the
  // root fix for the "piano roll only shows 1 bar" report — previously
  // `STEPS = 16` (a global constant) meant the grid was always 1 bar
  // wide regardless of stepWidth.
  const regionBars = Math.max(1, activeRegion?.length || 1);
  const totalSteps = Math.max(MIN_BARS_VISIBLE * BASE_STEPS_PER_BAR, Math.ceil(regionBars * BASE_STEPS_PER_BAR));

  useEffect(() => {
    return engine.onStep((s) => setPlayingStep(s));
  }, []);

  // v19.23 — Track the last mousedown target so the Piano Roll only
  // captures Delete when the user's most recent interaction was
  // actually inside its DOM. Before this, clicking a MIDI region in
  // the arranger while the Piano Roll tab was open caused Delete to
  // silently no-op — the capture-phase handler here would swallow it
  // (region-select set `activeRegion`, note-`selectedIds.size===0`,
  // so it prevented default and returned without deleting anything).
  // Now we consult the ref: if last click was outside the piano roll,
  // let Delete bubble through to the Arranger's handler.
  const pianoRollDomRef = useRef(null);
  const lastClickInPianoRollRef = useRef(false);
  useEffect(() => {
    const onDown = (e) => {
      lastClickInPianoRollRef.current = !!(pianoRollDomRef.current && pianoRollDomRef.current.contains(e.target));
    };
    window.addEventListener("mousedown", onDown, true);
    return () => window.removeEventListener("mousedown", onDown, true);
  }, []);

  // Keyboard shortcuts: Delete/Backspace → remove selected notes;
  // Ctrl/Cmd+D → duplicate selected notes one paintGridSize to the right.
  // Placed BEFORE any early-return so React's hooks-order stays deterministic
  // (the melodicRegions.length===0 and !activeRegion returns below both bail
  // out of the render pipeline, so any hook declared after them would be
  // called conditionally — a React rules-of-hooks violation).
  useEffect(() => {
    const onKey = (e) => {
      if (!activeRegion) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      // v19.15 P0 — When the Piano Roll is the active editor, Delete
      // must ALWAYS be captured here (even if no notes are selected)
      // so the Arranger's bubble-phase Delete handler cannot nuke the
      // region the user is editing.
      //
      // v19.23 — But "active editor" means "user's last click was
      // inside this Piano Roll". If the user just clicked a region in
      // the ARRANGER, they clearly want to delete that region — let
      // the Delete bubble to the Arranger handler.
      if (e.key === "Delete" || e.key === "Backspace") {
        if (!lastClickInPianoRollRef.current) return; // Let Arranger handle it.
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (selectedIds.size === 0) return; // Nothing to delete — silent no-op.
        // Note-only delete. The Arranger has its OWN Delete handler on
        // `window` (bubble phase) that removes the entire region when
        // `store.selectedIds` still contains the region id that was
        // double-clicked to open this piano roll. stopImmediatePropagation
        // above blocks it from firing so we can safely remove notes here.
        _snapshot();
        selectedIds.forEach((id) => removeRegionNote(activeRegion.id, id));
        setSelectedIds(new Set());
        return;
      }
      if (selectedIds.size === 0) return;
      if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
        // v12.6 — INTRA-REGION note duplication. The copies are placed
        // immediately adjacent to the selection (shifted by the exact
        // selection SPAN, not the paint grid size), and the region
        // auto-extends if the copies land past its current length.
        // stopPropagation prevents DAWPage's global handler (which
        // duplicates the WHOLE region) from ALSO firing — the correct
        // behaviour when the Piano Roll is focused is note-only dupe.
        e.preventDefault();
        e.stopPropagation();
        _snapshot();
        const ids = Array.from(selectedIds);
        const newIds = useDAWStore.getState().duplicateSelectedNotesInRegion(activeRegion.id, ids);
        // Move selection to the freshly-created copies so a follow-up
        // Cmd+D keeps chaining right.
        if (newIds.length > 0) setSelectedIds(new Set(newIds));
      } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        // v19.12 — Piano Roll transpose. Up/Down = ±1 semitone,
        // Shift+Up/Down = ±12 = one octave. Applies to every selected
        // note in the active region. stopPropagation prevents the
        // Arranger's outer handler (which transposes the WHOLE region)
        // from double-firing when the piano roll has focus.
        e.preventDefault();
        e.stopPropagation();
        const dir = e.key === "ArrowUp" ? 1 : -1;
        const delta = e.shiftKey ? 12 * dir : dir;
        _snapshot();
        selectedIds.forEach((id) => {
          const note = (activeRegion.notes || []).find((n) => n.id === id);
          if (!note) return;
          const newPitch = _transpose(note.pitch, delta);
          if (newPitch !== note.pitch) {
            updateRegionNote(activeRegion.id, id, { pitch: newPitch });
          }
        });
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [selectedIds, activeRegion, paintGridSize, addRegionNote, removeRegionNote, _snapshot]);

  if (melodicRegions.length === 0) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="piano-roll">
        <div className="text-center text-white/40 text-sm max-w-lg">
          <div>No MIDI regions on synth or sampler tracks.</div>
          <div className="label-mono mt-2">
            1. Drag <span className="text-white/80">Galaxy Synth</span> or <span className="text-white/80">Sampler</span> onto a track.<br />
            2. Click the <span className="text-white/80">+</span> button on that track to add a MIDI region.<br />
            3. Double-click the region to open it here for editing.
          </div>
        </div>
      </div>
    );
  }


  if (!activeRegion) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="piano-roll">
        <div className="text-center text-white/50 text-sm max-w-md">
          <div className="font-semibold mb-2">Select a region to edit</div>
          <div className="label-mono">Double-click any blue MIDI region on the timeline to open it here. Editing without a region is disabled.</div>
          <select
            onChange={(e) => selectRegion(e.target.value)}
            value=""
            className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs font-mono mt-4 focus:outline-none"
            data-testid="pr-region-select-empty"
          >
            <option value="">— pick a region —</option>
            {melodicRegions.map((r) => {
              const trk = tracks.find((t) => t.id === r.trackId);
              return <option key={r.id} value={r.id}>{trk?.name} · {r.name} · Bar {r.bar + 1}</option>;
            })}
          </select>
        </div>
      </div>
    );
  }

  // v12.7 — REMOVED the v12.6 auto-extend behaviour. Region length on
  // the arrangement grid is now controlled MANUALLY by the producer
  // (dragging the region's right edge). Notes placed past the region's
  // current length in the Piano Roll still exist in the region's notes
  // array but won't play until the producer manually extends the
  // region. This gives full manual control per user spec.

  const handleGridClick = (e, pitch) => {
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const gridSize = tool === "paint" ? paintGridSize : 1;
    const rawStep = x / stepWidth;
    const step = Math.floor(rawStep / gridSize) * gridSize;
    if (step < 0 || step >= totalSteps) return;
    if (tool === "paint") {
      addRegionNote(activeRegion.id, { step, pitch, duration: gridSize });
      if (activeTrack) engine.audition(activeTrack, pitch);
      return;
    }
    const existing = (activeRegion.notes || []).find((n) => n.pitch === pitch && Math.abs(n.step - step) < 1e-6);
    if (existing) {
      removeRegionNote(activeRegion.id, existing.id);
    } else {
      addRegionNote(activeRegion.id, { step, pitch, duration: 1 });
      if (activeTrack) engine.audition(activeTrack, pitch);
    }
  };

  // Paint drag: while mouse is down and tool='paint', laying down a stream of
  // notes as the cursor moves across the grid. Skips a cell if the paint
  // stamp for that (pitch, step) has already been placed in this stroke.
  const handleGridMouseDown = (e) => {
    if (!activeRegion || !gridRef.current) return;
    // Paint mode → existing paint stroke handler.
    if (tool === "paint") {
      const rect = gridRef.current.getBoundingClientRect();
      _snapshot();
      paintingRef.current = { stamps: new Set() };
      const paintAt = (clientX, clientY) => {
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const row = Math.floor(y / rowHeight);
        const rawStep = x / stepWidth;
        const step = Math.max(0, Math.floor(rawStep / paintGridSize) * paintGridSize);
        const pitch = NOTES[Math.max(0, Math.min(NOTES.length - 1, row))];
        if (step >= totalSteps) return;
        const key = `${pitch}@${step}`;
        if (paintingRef.current?.stamps.has(key)) return;
        paintingRef.current?.stamps.add(key);
        addRegionNote(activeRegion.id, { step, pitch, duration: paintGridSize });
      };
      paintAt(e.clientX, e.clientY);
      const onMove = (ev) => paintAt(ev.clientX, ev.clientY);
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        paintingRef.current = null;
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      return;
    }
    // Select mode → marquee bounding-box selection. Only starts when the
    // click landed on the empty grid (a note click bubbles up to the note's
    // mousedown handler and stopPropagates first).
    const rect = gridRef.current.getBoundingClientRect();
    const startX = e.clientX - rect.left;
    const startY = e.clientY - rect.top;
    setMarquee({ x0: startX, y0: startY, x1: startX, y1: startY });
    const onMove = (ev) => {
      setMarquee({ x0: startX, y0: startY, x1: ev.clientX - rect.left, y1: ev.clientY - rect.top });
    };
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const finalX = ev.clientX - rect.left;
      const finalY = ev.clientY - rect.top;
      const dx = Math.abs(finalX - startX);
      const dy = Math.abs(finalY - startY);
      if (dx < 4 && dy < 4) { setMarquee(null); setSelectedIds(new Set()); return; }
      const xL = Math.min(startX, finalX), xR = Math.max(startX, finalX);
      const yT = Math.min(startY, finalY), yB = Math.max(startY, finalY);
      const stepL = xL / stepWidth, stepR = xR / stepWidth;
      const rowT = Math.floor(yT / rowHeight), rowB = Math.floor(yB / rowHeight);
      const picked = new Set();
      for (const n of (activeRegion.notes || [])) {
        const rowIdx = NOTES.indexOf(n.pitch);
        const noteRight = n.step + (n.duration || 1);
        if (rowIdx >= rowT && rowIdx <= rowB && noteRight > stepL && n.step < stepR) {
          picked.add(n.id);
        }
      }
      setSelectedIds(picked);
      setMarquee(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Keyboard shortcuts moved up above the `if (!activeRegion) return` early
  // return to keep React's hooks-order deterministic — see the useEffect
  // near the top of this component.


  const handleNoteMouseDown = (e, note) => {
    e.stopPropagation();
    if (tool === "paint") return; // paint mode ignores existing-note picks — click elsewhere to lay down more
    const isResize = e.target.dataset?.resize === "true";
    const startX = e.clientX;
    const startY = e.clientY;
    const startPitchRow = NOTES.indexOf(note.pitch);
    const startDuration = note.duration || 1;
    // v19.10 — Multi-note mouse drag. If the clicked note is part of the
    // current multi-selection (>1), move the WHOLE group by the same
    // delta on every mousemove. If the click landed on an UN-selected
    // note, reset selection to just this note (standard DAW behavior)
    // so the drag operates on it alone.
    //
    // The keyboard nudge (Cmd+Arrow) already applies the delta to every
    // selected note; this brings mouse drag to parity.
    const notesArr = activeRegion?.notes || [];
    let dragGroup;
    if (selectedIds.size > 1 && selectedIds.has(note.id) && !isResize) {
      dragGroup = notesArr
        .filter((n) => selectedIds.has(n.id))
        .map((n) => ({ id: n.id, startStep: n.step ?? 0, startPitchRow: NOTES.indexOf(n.pitch) }));
    } else {
      if (!selectedIds.has(note.id)) setSelectedIds(new Set([note.id]));
      dragGroup = [{ id: note.id, startStep: note.step ?? 0, startPitchRow }];
    }
    // Take one snapshot at the start of the drag so Ctrl+Z reverts the whole
    // gesture as a single action instead of one per mousemove tick.
    _snapshot();

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const raw = dx / stepWidth;
      const dSteps = ev.shiftKey ? Math.round(raw) : raw;
      if (isResize) {
        const newDur = Math.max(0.25, Math.min(totalSteps - note.step, startDuration + dSteps));
        updateRegionNote(activeRegion.id, note.id, { duration: newDur });
      } else {
        // Vertical drag → change pitch. NOTES is highest-first, so a positive
        // dy (mouse moved DOWN) increases the row index → lower pitch.
        const dRows = Math.round(dy / rowHeight);
        // v19.10 — Apply the same dStep + dRow delta to every note in
        // the drag group. `clampStep` guarantees no note is pushed past
        // either edge of the region (dragging the group's leftmost note
        // to step 0 doesn't slam every other note to 0 — instead the
        // whole group's leftward motion is capped when ANY member would
        // otherwise go negative).
        const groupMinStart = Math.min(...dragGroup.map((g) => g.startStep));
        const clampedDSteps = Math.max(-groupMinStart, dSteps);
        const groupMinRow = Math.min(...dragGroup.map((g) => g.startPitchRow));
        const groupMaxRow = Math.max(...dragGroup.map((g) => g.startPitchRow));
        const clampedDRows = Math.max(-groupMinRow, Math.min(NOTES.length - 1 - groupMaxRow, dRows));
        let firstPitchChanged = null;
        for (const g of dragGroup) {
          const gNote = notesArr.find((n) => n.id === g.id);
          const dur = gNote?.duration || 1;
          const newStep = Math.max(0, Math.min(totalSteps - dur, g.startStep + clampedDSteps));
          const newRow = g.startPitchRow + clampedDRows;
          const newPitch = NOTES[newRow];
          const patch = { step: newStep };
          if (newPitch && newPitch !== gNote?.pitch) {
            patch.pitch = newPitch;
            if (firstPitchChanged === null) firstPitchChanged = newPitch;
          }
          updateRegionNote(activeRegion.id, g.id, patch);
        }
        // Audition the freshly-selected pitch once per mousemove tick.
        if (firstPitchChanged && activeTrack) engine.audition(activeTrack, firstPitchChanged);
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleNoteRightClick = (e, note) => {
    e.preventDefault();
    e.stopPropagation();
    removeRegionNote(activeRegion.id, note.id);
  };

  const isBlackKey = (n) => n.includes("#");

  // v13.1 — Quantize helpers. Applied to the current selection (or all
  // notes if nothing is selected). Both actions snapshot for undo.
  const SCALE_INTERVALS = {
    major: [0, 2, 4, 5, 7, 9, 11],
    minor: [0, 2, 3, 5, 7, 8, 10],
  };
  const TONIC_ORDER = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
  const parseMidi = (pitchStr) => {
    const m = pitchStr.match(/^([A-G]#?)(-?\d+)$/);
    if (!m) return 60;
    return TONIC_ORDER.indexOf(m[1]) + (parseInt(m[2], 10) + 1) * 12;
  };
  const midiToPitch = (midi) => {
    const oct = Math.floor(midi / 12) - 1;
    return `${TONIC_ORDER[midi % 12]}${oct}`;
  };
  const snapMidiToScale = (midi, tonic, mode) => {
    const tonicIdx = TONIC_ORDER.indexOf(tonic);
    if (tonicIdx < 0) return midi;
    const intervals = SCALE_INTERVALS[mode] || SCALE_INTERVALS.major;
    // Build a set of allowed pitch-classes for this scale.
    const allowed = new Set(intervals.map((i) => (tonicIdx + i) % 12));
    if (allowed.has(midi % 12)) return midi;
    // Nearest scale degree — search up/down at most 6 semitones.
    for (let d = 1; d <= 6; d++) {
      if (allowed.has((midi + d) % 12)) return midi + d;
      if (allowed.has(((midi - d) % 12 + 12) % 12)) return midi - d;
    }
    return midi;
  };
  const getTargetNoteIds = () => {
    const notes = activeRegion?.notes || [];
    if (selectedIds.size > 0) return notes.filter((n) => selectedIds.has(n.id));
    return notes;
  };
  const handleQuantize = () => {
    // v13.1 (revised) — SINGLE Quantize action. Always snaps step to
    // grid; ALSO snaps pitch to the selected scale (if one is chosen
    // in the dropdown). One click / Q keyboard shortcut = both ops.
    if (!activeRegion) return;
    const targets = getTargetNoteIds();
    if (targets.length === 0) return;
    _snapshot();
    const grid = Math.max(0.25, paintGridSize);
    const [tonic, mode] = (scaleKey || "").split(":");
    const scaleActive = tonic && mode && SCALE_INTERVALS[mode];
    targets.forEach((n) => {
      const patch = {};
      const snappedStep = Math.round((n.step || 0) / grid) * grid;
      if (snappedStep !== n.step) patch.step = snappedStep;
      if (scaleActive) {
        const midi = parseMidi(n.pitch);
        const snappedMidi = snapMidiToScale(midi, tonic, mode);
        if (snappedMidi !== midi) patch.pitch = midiToPitch(snappedMidi);
      }
      if (Object.keys(patch).length > 0) {
        updateRegionNote(activeRegion.id, n.id, patch);
      }
    });
  };

  return (
    <div ref={pianoRollDomRef} className="h-full flex flex-col" data-testid="piano-roll">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#222]">
        <div className="flex items-center gap-3">
          <h3 className="font-display font-bold text-sm">Piano Roll</h3>
          <select
            value={activeRegion.id}
            onChange={(e) => selectRegion(e.target.value)}
            className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-[#444]"
            data-testid="piano-roll-region-select"
          >
            {melodicRegions.map((r) => {
              const trk = tracks.find((t) => t.id === r.trackId);
              return <option key={r.id} value={r.id}>{trk?.name} · {r.name} · Bar {r.bar + 1}</option>;
            })}
          </select>
          <span className="label-mono">
            {tool === "paint"
              ? `paint · click / drag to lay ${paintGridLabel(paintGridSize)} notes`
              : (activeTrack?.type === "sampler"
                  ? "notes pitch-shift the sample · drag vertically to change pitch · right-click to delete"
                  : "click cells · drag to move + change pitch · right-click to delete")}
          </span>
        </div>
        {/* Tool switcher + paint quantization */}
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md overflow-hidden" style={{ border: "1px solid #262626" }}>
            <button
              onClick={() => setTool("select")}
              className={`px-2 py-1 text-[11px] font-bold flex items-center gap-1 ${tool === "select" ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
              data-testid="pr-tool-select"
              title="Select / draw / drag"
            >
              <Cursor size={11} weight="fill" /> Select
            </button>
            <button
              onClick={() => setTool("paint")}
              className={`px-2 py-1 text-[11px] font-bold flex items-center gap-1 border-l border-[#262626] ${tool === "paint" ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80"}`}
              data-testid="pr-tool-paint"
              title="Paintbrush — drag to lay notes at grid resolution"
            >
              <PaintBrush size={11} weight="fill" /> Paint
            </button>
          </div>
          {tool === "paint" && (
            <select
              value={String(paintGridSize)}
              onChange={(e) => setPaintGridSize(parseFloat(e.target.value))}
              className="bg-[#0a0a0a] border border-[#262626] rounded px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-[#444]"
              data-testid="pr-paint-grid"
              title="Paintbrush grid resolution"
            >
              <option value="2">1/8</option>
              <option value="1">1/16</option>
              <option value="0.5">1/32</option>
              <option value="0.25">1/64</option>
            </select>
          )}
          {/* v13.1 (revised) — SINGLE Quantize button (Q). Always snaps
              step to grid; ALSO snaps pitch to the selected scale when
              one is chosen in the dropdown. */}
          <button
            onClick={handleQuantize}
            className="btn-3d px-2 py-1 text-[11px] font-bold flex items-center gap-1"
            title="Quantize (Q) — snap steps to grid + snap pitches to selected scale"
            data-testid="pr-quantize"
          >
            Q
          </button>
          {/* Scale-key dropdown feeds the Q action above. */}
          <select
            value={scaleKey}
            onChange={(e) => setScaleKey(e.target.value)}
            className="bg-[#0a0a0a] border border-[#262626] rounded px-2 py-1 text-[11px] font-mono focus:outline-none focus:border-[#444]"
            data-testid="pr-scale-select"
            title="Scale for Q to snap pitches into (leave blank for grid-only Q)"
          >
            <option value="">— key —</option>
            {["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"].flatMap((k) => [
              <option key={`${k}:major`} value={`${k}:major`}>{k} Major</option>,
              <option key={`${k}:minor`} value={`${k}:minor`}>{k} Minor</option>,
            ])}
          </select>
        </div>
        <button
          onClick={() => clearRegionNotes(activeRegion.id)}
          className="btn-3d px-3 py-1.5 text-xs flex items-center gap-1.5"
          data-testid="clear-notes-btn"
        >
          <Trash size={12} weight="bold" /> Clear
        </button>
        <div className="relative ml-2">
          <button
            onClick={() => setShowZoom((s) => !s)}
            className="btn-3d px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5"
            data-testid="pr-zoom-btn"
          >
            <GearSix size={12} weight="bold" /> Zoom
          </button>
          {showZoom && (
            <div className="absolute right-0 top-9 z-30 plugin-panel p-4 w-72" data-testid="pr-zoom-popover">
              <div className="font-display font-bold text-xs mb-3">Piano Roll Zoom</div>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="label-mono flex items-center gap-1"><ArrowsOutLineHorizontal size={11} weight="bold" /> Horizontal · {stepWidth}px/step</span>
                  </div>
                  <input type="range" min="16" max="120" step="2" value={stepWidth}
                    onChange={(e) => setStepWidth(+e.target.value)}
                    className="w-full accent-blue-500" data-testid="pr-horizontal-slider" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="label-mono flex items-center gap-1"><ArrowsOutLineVertical size={11} weight="bold" /> Vertical · {rowHeight}px/key</span>
                  </div>
                  <input type="range" min="10" max="40" step="1" value={rowHeight}
                    onChange={(e) => setRowHeight(+e.target.value)}
                    className="w-full accent-blue-500" data-testid="pr-vertical-slider" />
                </div>
                <div className="flex gap-2 pt-2 border-t border-[#262626]">
                  <button onClick={() => { setStepWidth(36); setRowHeight(18); }}
                    className="btn-3d flex-1 py-1.5 text-[11px]" data-testid="pr-zoom-reset">Reset</button>
                  <button
                    onClick={() => {
                      const container = gridRef.current?.parentElement?.parentElement?.parentElement;
                      if (!container) return;
                      const cw = container.clientWidth - HEADER_WIDTH - 40;
                      const ch = container.clientHeight - 50;
                      // v12.6 — Fit button targets 4 full bars (64 steps) in the viewport.
                      setStepWidth(Math.max(20, Math.floor(cw / (MIN_BARS_VISIBLE * BASE_STEPS_PER_BAR))));
                      setRowHeight(Math.max(10, Math.floor(ch / NOTES.length)));
                    }}
                    className="btn-3d flex-1 py-1.5 text-[11px]" data-testid="pr-zoom-fit">Fit</button>
                  <button onClick={() => setShowZoom(false)}
                    className="btn-3d-accent btn-3d flex-1 py-1.5 text-[11px] font-bold">Done</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div ref={scrollerRef} className="flex-1 overflow-auto p-3">
        <div className="inline-block">
          {/* steps header — v12.6: renders the full 4-bar (or region-length) span */}
          <div className="flex" style={{ paddingLeft: HEADER_WIDTH }}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div key={i}
                className={`text-center label-mono ${playingStep === i ? "text-[#00FF41]" : ""}`}
                style={{ width: stepWidth }}>
                {i + 1}
              </div>
            ))}
          </div>

          {/* Grid + notes */}
          <div className="flex">
            {/* Left keys column */}
            <div className="flex flex-col">
              {NOTES.map((note) => (
                <div
                  key={note}
                  className={`flex items-center justify-end pr-2 text-[10px] font-mono border-r border-[#222] ${isBlackKey(note) ? "pr-key-black text-[#666]" : "pr-key-white text-[#999]"}`}
                  style={{ width: HEADER_WIDTH, height: rowHeight }}
                  onClick={() => activeTrack && engine.audition(activeTrack, note)}
                >
                  {note}
                </div>
              ))}
            </div>

            {/* Grid */}
            <div
              ref={gridRef}
              className="relative"
              onMouseDown={handleGridMouseDown}
              style={{
                width: totalSteps * stepWidth,
                height: NOTES.length * rowHeight,
                cursor: tool === "paint" ? "crosshair" : "default",
              }}
            >
              {/* Row backgrounds */}
              {NOTES.map((note, ri) => (
                <div
                  key={note}
                  onClick={(e) => handleGridClick(e, note)}
                  className={`absolute left-0 right-0 cursor-pointer ${isBlackKey(note) ? "bg-[#0c0c0c]" : "bg-[#141414]"} hover:bg-[#1c1c1c]`}
                  style={{ top: ri * rowHeight, height: rowHeight, borderBottom: "1px solid #1f1f1f" }}
                />
              ))}

              {/* Step beat lines — v12.6: full-grid range */}
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className="absolute top-0 bottom-0 pointer-events-none"
                  style={{
                    left: i * stepWidth,
                    borderLeft: i % 4 === 0 ? "1px solid #2a2a2a" : "1px solid #1a1a1a",
                  }}
                />
              ))}

              {/* Playhead */}
              {playingStep >= 0 && (
                <div className="absolute top-0 bottom-0 w-px bg-[#00FF41] pointer-events-none z-20"
                  style={{ left: playingStep * stepWidth + stepWidth / 2, boxShadow: "0 0 4px #00FF41" }} />
              )}

              {/* Notes */}
              {(activeRegion.notes || []).map((note) => {
                const row = NOTES.indexOf(note.pitch);
                if (row === -1) return null;
                const dur = note.duration || 1;
                const isSelected = selectedIds.has(note.id);
                return (
                  <div
                    key={note.id}
                    className="absolute cursor-grab active:cursor-grabbing"
                    style={{
                      top: row * rowHeight + 1,
                      left: note.step * stepWidth + 1,
                      width: dur * stepWidth - 2,
                      height: rowHeight - 2,
                      background: `linear-gradient(180deg, ${activeTrack.color}, ${activeTrack.color}AA)`,
                      border: isSelected ? `2px solid #fef08a` : `1px solid ${activeTrack.color}`,
                      borderRadius: 3,
                      boxShadow: isSelected
                        ? `0 0 0 2px rgba(254,240,138,0.4), inset 0 1px 0 rgba(255,255,255,0.3)`
                        : `inset 0 1px 0 rgba(255,255,255,0.3), 0 2px 3px rgba(0,0,0,0.5)`,
                    }}
                    onMouseDown={(e) => handleNoteMouseDown(e, note)}
                    onContextMenu={(e) => handleNoteRightClick(e, note)}
                    data-testid={`pr-note-${note.id}`}
                    data-selected={isSelected || undefined}
                    title="drag to move · right edge to resize · right-click to delete"
                  >
                    <div
                      data-resize="true"
                      className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40"
                      style={{ borderTopRightRadius: 3, borderBottomRightRadius: 3 }}
                    />
                  </div>
                );
              })}
              {/* Marquee bounding-box overlay */}
              {marquee && (
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: Math.min(marquee.x0, marquee.x1),
                    top: Math.min(marquee.y0, marquee.y1),
                    width: Math.abs(marquee.x1 - marquee.x0),
                    height: Math.abs(marquee.y1 - marquee.y0),
                    background: "rgba(254,240,138,0.10)",
                    border: "1px dashed #fef08a",
                    borderRadius: 2,
                  }}
                  data-testid="pr-marquee"
                />
              )}
            </div>
          </div>

          {/* v13.1 — VELOCITY LANE. Renders a horizontal strip below the
              grid with one vertical bar per note whose height = velocity
              (default 100 / MIDI 127). Click-drag a bar to change that
              note's velocity in real-time. The lane's width matches the
              grid width (totalSteps × stepWidth) and starts after the
              same HEADER_WIDTH-wide gap so the bars line up 1:1 with
              their note positions above. */}
          <div className="flex" style={{ paddingLeft: HEADER_WIDTH, marginTop: 8 }}>
            <div
              className="relative border-t border-[#333]"
              style={{
                width: totalSteps * stepWidth,
                height: VELOCITY_LANE_HEIGHT,
                background: "#0a0a0a",
              }}
              data-testid="pr-velocity-lane"
            >
              <div
                className="absolute left-1 top-1 text-[9px] font-mono uppercase text-white/40 pointer-events-none z-10"
                style={{ letterSpacing: "0.05em" }}
              >
                velocity
              </div>
              {(activeRegion.notes || []).map((note) => {
                const dur = Math.max(0.25, note.duration || 1);
                const vel = typeof note.velocity === "number" ? note.velocity : 100;
                const clamped = Math.max(0, Math.min(127, vel));
                const barH = Math.max(2, Math.round((clamped / 127) * (VELOCITY_LANE_HEIGHT - 6)));
                const barW = Math.max(4, dur * stepWidth - 2);
                const left = (note.step || 0) * stepWidth + 1;
                const isSel = selectedIds.has(note.id);
                const onDown = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  _snapshot();
                  const laneEl = e.currentTarget.parentElement;
                  const rect = laneEl.getBoundingClientRect();
                  const onMove = (ev) => {
                    const y = ev.clientY - rect.top;
                    const usable = VELOCITY_LANE_HEIGHT - 6;
                    const fromBottom = Math.max(0, Math.min(usable, usable - (y - 3)));
                    const nextVel = Math.round((fromBottom / usable) * 127);
                    updateRegionNote(activeRegion.id, note.id, { velocity: nextVel });
                  };
                  const onUp = () => {
                    window.removeEventListener("mousemove", onMove);
                    window.removeEventListener("mouseup", onUp);
                  };
                  onMove(e);
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                };
                return (
                  <div
                    key={`vel-${note.id}`}
                    className="absolute bottom-0 cursor-ns-resize"
                    style={{
                      left,
                      width: barW,
                      height: barH,
                      background: isSel
                        ? "linear-gradient(180deg, #fef08a, #f59e0b)"
                        : `linear-gradient(180deg, ${activeTrack.color}, ${activeTrack.color}AA)`,
                      border: `1px solid ${isSel ? "#fef08a" : activeTrack.color}`,
                      borderBottom: "none",
                      borderRadius: "2px 2px 0 0",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
                    }}
                    onMouseDown={onDown}
                    title={`velocity ${clamped} — drag vertically to change`}
                    data-testid={`pr-velocity-${note.id}`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
